import { Injectable, OnDestroy } from '@angular/core';
import { EMPTY, Observable, Subscription, timer } from 'rxjs';
import { RefreshTokenData } from '../../models/session/refresh-token-request.interface';
import { HttpClient } from '@angular/common/http';
import { shareReplay, switchMap, distinctUntilChanged, startWith, map, filter, finalize, tap } from 'rxjs/operators';
import { LocalStorageService, constants } from '@edassist/common';
import { JwtUtil } from '@tuition/app/core/util/jwt.util';
import { RefreshLockService } from '../refresh-lock/refresh-lock.service';

@Injectable({
	providedIn: 'root'
})
export class TokenService implements OnDestroy {
	readonly token$: Observable<RefreshTokenData>;
	private readonly refreshSubscription: Subscription = new Subscription();
	private refreshSubscriptionAlreadyExists = false;

	set token(token: string) {
		const ngStorageTokens = LocalStorageService.getItem(constants.localStorageKeys.TOKENS_HYBRID) ?? {};
		ngStorageTokens.tokens = {
			...ngStorageTokens.tokens,
			sessionToken: token
		};
		LocalStorageService.setItem(constants.localStorageKeys.TOKENS_HYBRID, ngStorageTokens);
	}

	get token(): string {
		// First check hybrid tokens, then session token, fallback to empty string
		// Empty string prevents HTTP Interceptor errors on null values
		return LocalStorageService.getItem(constants.localStorageKeys.TOKENS_HYBRID)?.tokens?.sessionToken ?? '';
	}

	constructor(
		private readonly http: HttpClient,
		private readonly refreshLockService: RefreshLockService
	) {
		this.token$ = LocalStorageService.storageChanges$.pipe(
			filter((c) => c?.key === constants.localStorageKeys.TOKENS_HYBRID),
			map((c) => {
				const session = c.newValue ? JSON.parse(c.newValue) : null;

				return session?.tokens as RefreshTokenData;
			}),
			startWith(LocalStorageService.getItem(constants.localStorageKeys.TOKENS_HYBRID)?.tokens as RefreshTokenData),
			distinctUntilChanged((prev, curr) => {
				return prev?.refreshToken === curr?.refreshToken && prev?.sessionToken === curr?.sessionToken;
			}),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	ngOnDestroy(): void {
		this.refreshSubscription?.unsubscribe();
	}

	isTokenValid(token?: RefreshTokenData): boolean {
		return !!token && !JwtUtil.isExpired(token?.sessionToken);
	}

	setTokenRefreshInterval(): void {
		if (this.refreshSubscriptionAlreadyExists) {
			console.log('Refresh subscription already exists, skipping setup');

			return;
		}

		this.refreshSubscription.add(this.setupRefreshToken().subscribe());
		this.refreshSubscriptionAlreadyExists = true;
	}

	private refreshToken(oldToken: RefreshTokenData): Observable<RefreshTokenData> {
		console.log('oldToken', oldToken);

		return this.http.post<RefreshTokenData>('sessions/refresh-token', { ...oldToken }).pipe(
			tap((t) => {
				LocalStorageService.setItem(constants.localStorageKeys.TOKENS_HYBRID, {
					...LocalStorageService.getItem(constants.localStorageKeys.TOKENS_HYBRID),
					tokens: t
				});
			})
		);
	}

	private setupRefreshToken() {
		return this.token$.pipe(
			switchMap((token) => {
				if (!token) return EMPTY;
				const msUntilExpiration = JwtUtil.msUntilExpiration(token?.sessionToken);
				if (!msUntilExpiration || msUntilExpiration <= 0) {
					console.log('Token is expired or invalid, skipping refresh');

					return EMPTY;
				}
				// Schedule refresh 1 minute before expiration
				const ms = msUntilExpiration - 60_000;
				console.log(`Scheduling token refresh in ${ms} ms`);

				return timer(Math.max(ms, 0)).pipe(map(() => token));
			}),
			switchMap((oldToken) => {
				if (!oldToken) return EMPTY;
				if (!this.refreshLockService.tryLock()) return EMPTY;
				console.log('Acquired refresh lock, proceeding with token refresh');

				return this.refreshToken(oldToken).pipe(finalize(() => this.refreshLockService.release()));
			})
		);
	}
}
